import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../../constants/theme';
import RecruitmentInsightsService, { RecruitmentInsight } from '../../services/RecruitmentInsightsService';
import RecruitmentInsightCard from './RecruitmentInsightCard';
import { AnimatedCounter } from '../MicroInteractions';

const WidgetContainer = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.xl}px;
  box-shadow: ${Shadows.sm};
  position: relative;
`;

const WidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.lg}px;
`;

const WidgetTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
`;

const WidgetIcon = styled.span`
  font-size: ${FontSizes.xl}px;
`;

const ViewAllButton = styled.button`
  background: ${Colors.surface};
  color: ${Colors.primary};
  border: 1px solid ${Colors.border};
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
    border-color: ${Colors.primary};
    box-shadow: ${Shadows.sm};
  }
`;

const KPIStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${Spacing.md}px;
  margin-bottom: ${Spacing.lg}px;
`;

const KPICard = styled.div<{ alert?: boolean }>`
  background: ${props => props.alert ? `${Colors.danger}05` : Colors.surface};
  border: 1px solid ${props => props.alert ? `${Colors.danger}30` : Colors.borderLight};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.md}px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    background: ${Colors.surfaceHover};
    box-shadow: ${Shadows.sm};
    border-color: ${props => props.alert ? Colors.danger : Colors.primary};
  }
  
  ${props => props.alert && `
    &::after {
      content: '!';
      position: absolute;
      top: ${Spacing.xs}px;
      right: ${Spacing.xs}px;
      width: 16px;
      height: 16px;
      background: ${Colors.danger};
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: ${FontSizes.xs}px;
      font-weight: ${FontWeights.bold};
    }
  `}
`;

const KPIValue = styled.div`
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
  display: flex;
  align-items: baseline;
  gap: ${Spacing.xs}px;
`;

const KPIUnit = styled.span`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.normal};
  color: ${Colors.textSecondary};
`;

const KPILabel = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${Spacing.xs}px;
`;

const KPITrend = styled.div<{ positive?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  font-size: ${FontSizes.xs}px;
  color: ${props => props.positive ? Colors.success : Colors.danger};
  font-weight: ${FontWeights.medium};
`;

const InsightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

const MarketAlert = styled.div`
  background: ${Colors.warning}10;
  border: 1px solid ${Colors.warning}30;
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.md}px;
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
`;

const AlertIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${Colors.warning}20;
  border-radius: ${BorderRadius.sm}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.lg}px;
`;

const AlertContent = styled.div`
  flex: 1;
`;

const AlertTitle = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
`;

const AlertDescription = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
`;

const AlertAction = styled.button`
  background: ${Colors.warning};
  color: ${Colors.textOnPrimary};
  border: none;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xl}px;
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.sm}px;
`;

interface TalentAcquisitionWidgetProps {
  department?: string;
  region?: string;
  className?: string;
}

const TalentAcquisitionWidget: React.FC<TalentAcquisitionWidgetProps> = ({
  department,
  region,
  className
}) => {
  const navigate = useNavigate();
  const currentRole = useSelector((state: RootState) => state.user.currentRole);
  const [insights, setInsights] = useState<RecruitmentInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    loadInsights();
    
    // Set up real-time updates
    const unsubscribe = RecruitmentInsightsService.subscribeToUpdates(
      currentRole,
      { department, region },
      (newInsights) => {
        setInsights(newInsights);
        setLastUpdated(new Date());
      }
    );

    return () => unsubscribe();
  }, [currentRole, department, region]);

  const loadInsights = async () => {
    setLoading(true);
    try {
      const data = await RecruitmentInsightsService.getHRLeaderInsights(department, region);
      const maskedData = RecruitmentInsightsService.applyPrivacyMasking(data, currentRole);
      setInsights(maskedData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load recruitment insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKPIClick = (insight: RecruitmentInsight) => {
    if (insight.drillThroughPath) {
      navigate(insight.drillThroughPath, {
        state: {
          fromWidget: 'talent_acquisition',
          metric: insight.title,
          role: currentRole,
          scope: { department, region }
        }
      });
    }
  };

  const handleViewAll = () => {
    navigate('/requisitions?view=strategic', {
      state: {
        fromWidget: 'talent_acquisition',
        role: currentRole,
        scope: { department, region }
      }
    });
  };

  if (loading) {
    return (
      <WidgetContainer className={className}>
        <LoadingState>Loading recruitment insights...</LoadingState>
      </WidgetContainer>
    );
  }

  // Extract key metrics for KPI strip
  const pipelineHealth = insights.find(i => i.type === 'pipeline_health');
  const timeToFill = insights.find(i => i.type === 'time_to_fill');
  const marketAlert = insights.find(i => i.type === 'market_alert');
  const diversity = insights.find(i => i.type === 'diversity');

  // Determine scope label
  const scopeLabel = department 
    ? `${department}${region ? ` - ${region}` : ''}`
    : region 
    ? region
    : 'Global';

  return (
    <WidgetContainer className={className}>
      <WidgetHeader>
        <WidgetTitle>
          Talent Acquisition
        </WidgetTitle>
        <ViewAllButton onClick={handleViewAll}>
          View Full Dashboard →
        </ViewAllButton>
      </WidgetHeader>

      <KPIStrip>
        {pipelineHealth && (
          <KPICard onClick={() => handleKPIClick(pipelineHealth)}>
            <KPILabel>Pipeline Health</KPILabel>
            <KPIValue>
              <AnimatedCounter value={Number(pipelineHealth.value)} />
              <KPIUnit>%</KPIUnit>
            </KPIValue>
            {pipelineHealth.change && (
              <KPITrend positive={pipelineHealth.changeType === 'positive'}>
                <span>{pipelineHealth.changeType === 'positive' ? '↑' : '↓'}</span>
                <span>{Math.abs(pipelineHealth.change)}%</span>
              </KPITrend>
            )}
          </KPICard>
        )}

        {timeToFill && (
          <KPICard onClick={() => handleKPIClick(timeToFill)}>
            <KPILabel>Avg. Time to Fill</KPILabel>
            <KPIValue>
              <AnimatedCounter value={Number(timeToFill.value)} />
              <KPIUnit>{timeToFill.unit}</KPIUnit>
            </KPIValue>
            {timeToFill.change && (
              <KPITrend positive={timeToFill.changeType === 'positive'}>
                <span>{timeToFill.change > 0 ? '↑' : '↓'}</span>
                <span>{Math.abs(timeToFill.change)}%</span>
              </KPITrend>
            )}
          </KPICard>
        )}

        {diversity && (
          <KPICard onClick={() => handleKPIClick(diversity)}>
            <KPILabel>Diversity Pipeline</KPILabel>
            <KPIValue>
              <AnimatedCounter value={Number(diversity.value)} />
              <KPIUnit>%</KPIUnit>
            </KPIValue>
            {diversity.change && (
              <KPITrend positive={diversity.changeType === 'positive'}>
                <span>{diversity.changeType === 'positive' ? '↑' : '↓'}</span>
                <span>{Math.abs(diversity.change)}%</span>
              </KPITrend>
            )}
          </KPICard>
        )}

        {marketAlert && marketAlert.priority === 'high' && (
          <KPICard alert onClick={() => handleKPIClick(marketAlert)}>
            <KPILabel>High Difficulty Roles</KPILabel>
            <KPIValue>
              <AnimatedCounter value={Number(marketAlert.value)} />
              <KPIUnit>{marketAlert.unit}</KPIUnit>
            </KPIValue>
            {marketAlert.change && (
              <KPITrend positive={false}>
                <span>↑</span>
                <span>{Math.abs(marketAlert.change)}</span>
              </KPITrend>
            )}
          </KPICard>
        )}
      </KPIStrip>

      {marketAlert && marketAlert.priority === 'high' && (
        <MarketAlert>
          <AlertIcon></AlertIcon>
          <AlertContent>
            <AlertTitle>Market Availability Alert</AlertTitle>
            <AlertDescription>
              {marketAlert.value} roles have high replacement difficulty in current market conditions.
              Average time-to-fill for these positions is 60% higher than baseline.
            </AlertDescription>
          </AlertContent>
          <AlertAction onClick={() => handleKPIClick(marketAlert)}>
            View Details
          </AlertAction>
        </MarketAlert>
      )}

      <InsightsList>
        <RecruitmentInsightCard
          title="Recruitment Metrics"
          subtitle={`Real-time recruitment data for ${scopeLabel}`}
          metrics={insights.map(i => ({
            label: i.title,
            value: i.value,
            change: i.change,
            changeType: i.changeType,
            unit: i.unit,
            drillThroughPath: i.drillThroughPath,
            minRecords: i.recordCount
          }))}
          sourceLabel="Recruitment Pipeline – Clouds on Mars"
          lastUpdated={lastUpdated}
          roleScopeLabel={scopeLabel}
        />
      </InsightsList>
    </WidgetContainer>
  );
};

export default TalentAcquisitionWidget;